import React, {
  useRef,
  useState,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
} from "react";
import { KEY_VALUES, MENU_PLACEMENT, MenuListItem } from "@cdkglobal/radial";
import {
  ALL_SELECTION_ENABLED_FIELD_KEYS,
  DROP_DOWN_OPTIONS,
  LOADING_DEFAULT_ISelectableOption,
  NO_RESULTS_DEFAULT_ISelectableOption,
} from "../../constants";
import { StyledAutocomplete } from "../../../../utils/styled";
import {
  ISelectableOption,
  RenderMultiAutocompleteMenuProps,
} from "../../../../types";
import {
  getRecordsBySearchFilter,
  dispatchDeletedObjectIfExists,
  dispatchDeletedObjectsForAll,
  getTitleFromTargetPropertyKey,
  getSearchValueUpdatedTypeAheadRequest,
  getSelectAllState,
  getSelectedOptionLabel,
} from "../../helpers";
import { getEmptyFieldsMessage } from "../../validations";
import { uniqueId } from "underscore";
import { useIntl } from "react-intl";
import { MultiInputWithTooltip } from "../../../common/Tooltip";
import styled, { css } from "styled-components";

const MultiAutocompleteInputWrapper = styled.div<{
  $isDisabled: boolean;
}>`
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      [data-testid$="-value"] svg {
        display: none;
      }

      label > svg {
        display: none;
      }
    `}
`;

const shouldUseTopMenuPlacement = (
  dimension: HTMLLabelElement | null,
): boolean => {
  return (
    !!dimension &&
    window.innerHeight - dimension.getBoundingClientRect().bottom < 200
  );
};

const handleEnterSelection = ({
  results,
  selectedOptions,
  defaultOptions,
  setSelectedOptions,
  setResults,
  setInputValue,
  dispatchSelectedOption,
  handleMenu,
}: {
  results: ISelectableOption[];
  selectedOptions: ISelectableOption[];
  defaultOptions: ISelectableOption[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<ISelectableOption[]>>;
  setResults: React.Dispatch<React.SetStateAction<ISelectableOption[]>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  dispatchSelectedOption: (option: ISelectableOption) => void;
  handleMenu: () => Promise<void>;
}): void => {
  if (results.length === 0 || results[0].disabled) {
    return;
  }

  const newSelectedValues = new Set([
    ...selectedOptions.map((option) => option.value),
    results[0].value,
  ]);

  const notSelectedOptions = defaultOptions.filter(
    (option) => !newSelectedValues.has(option.value),
  );

  setSelectedOptions((prev) => [...prev, results[0]]);
  setResults(notSelectedOptions);
  setInputValue("");
  dispatchSelectedOption(results[0]);
  void handleMenu();
};

export default function RenderMultiAutocompleteMenu({
  title,
  description,
  propertyKey,
  propertySubKey,
  rowIndex,
  nestedPropertySubKey,
  nestedRowIndex,
  inputValues,
  typeAheadRequest,
  optionsList,
  dynamicDependentFieldsAndValues,
  isRequired,
  isDisabled,
  error,
  message,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
  existingGroupIdKey,
  existingGroupIdValue,
  evalTypeAheadMethods,
  typeAheadConfig,
  maxItems,
}: Readonly<RenderMultiAutocompleteMenuProps>): JSX.Element {
  const intl = useIntl();
  const initialOptions = optionsList ?? [LOADING_DEFAULT_ISelectableOption()];
  const [defaultOptions, setDefaultOptions] =
    useState<ISelectableOption[]>(initialOptions);
  const dimensionRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSelectingRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [onUnselect, setOnUnselect] = useState(false);
  const [results, setResults] = useState<ISelectableOption[]>(initialOptions);
  const [selectedOptions, setSelectedOptions] = useState(inputValues);
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");

  useEffect(() => {
    const resultMap = new Map(results.map((result) => [result.value, result]));

    setSelectedOptions((previousOptions) => {
      const previousOptionMap = new Map(
        previousOptions.map((option) => [option.value, option]),
      );
      return inputValues.map(
        (option) =>
          resultMap.get(option.value) ??
          previousOptionMap.get(option.value) ??
          option,
      );
    });
  }, [inputValues, results]);

  const menuPlacement = shouldUseTopMenuPlacement(dimensionRef.current)
    ? MENU_PLACEMENT.TOP_START
    : MENU_PLACEMENT.BOTTOM_START;

  const isMaxItemsExceeded =
    maxItems !== undefined && inputValues.length > maxItems;

  const maxItemsErrorMessage = isMaxItemsExceeded
    ? intl.formatMessage(
        {
          id: "validation.maxItems",
          defaultMessage: "Maximum {maxItems} values allowed",
        },
        {
          maxItems,
        },
      )
    : "";

  const fieldKey = propertySubKey
    ? `${propertyKey}-${propertySubKey}`
    : propertyKey;

  const isSelectAllEnabled =
    ALL_SELECTION_ENABLED_FIELD_KEYS.includes(fieldKey);

  const { selectableOptions, isAllSelected, isIndeterminate } =
    getSelectAllState(defaultOptions, selectedOptions);

  const hasSelectableOptions = selectableOptions.length > 0;

  const getOptionLabel = (option: ISelectableOption) =>
    getSelectedOptionLabel(option, selectedOptions);

  const multiInputValues =
    isSelectAllEnabled && inputValues.length > 0
      ? [`${inputValues.length} Selected`]
      : inputValues.map(getOptionLabel);

  const resetSelections = () => {
    setSelectedOptions([]);
    setInputValue("");
    setResults(defaultOptions);

    dispatch({
      type: "REMOVE_MULTI_VALUED_FIELD",
      payload: {
        propertyKey,
        propertySubKey,
        newFieldValue: null,
        nestedPropertySubKey,
        nestedRowIndex,
        selectedTabSectionKey,
      },
    });

    dispatchDeletedObjectsForAll({
      propertyKey,
      propertySubKey,
      nestedRowIndex,
      rowData: rowDataReducerState.rowData,
      selectedTabSectionKey,
      dispatch,
    });

    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }

    setIsOpen(false);
    setInputValue("");
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) return;

    event.preventDefault();
    resetSelections();
  };

  const handleMenu = async () => {
    setIsOpen((prev) => !prev);
    setOnUnselect((prev) => !prev);
    await fetchSearchOptions();
  };

  const isOptionSelected = (option: ISelectableOption): boolean => {
    return (
      selectedOptions
        .map((selectedOption) => selectedOption.label)
        .includes(option.label) ||
      (selectedOptions
        .map((selectedOption) => selectedOption.value)
        .includes(option.value) &&
        option.value !== "")
    );
  };

  const removeValue = (index: number) => {
    if (isDisabled || index < 0) return;

    setSelectedOptions((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );

    if (propertySubKey) {
      dispatch({
        type: "REMOVE_MULTI_VALUED_FIELD",
        payload: {
          propertyKey,
          propertySubKey,
          rowIndex: index,
          nestedPropertySubKey,
          nestedRowIndex,
          selectedTabSectionKey,
        },
      });
    }

    dispatchDeletedObjectIfExists({
      propertyKey,
      propertySubKey,
      nestedRowIndex,
      index,
      rowData: rowDataReducerState.rowData,
      selectedTabSectionKey,
      dispatch,
    });
  };

  const dispatchSelectedOption = (option: ISelectableOption) => {
    dispatch({
      type: "ADD_MULTI_VALUED_FIELD",
      payload: {
        propertyKey,
        rowIndex,
        newFieldValue: option.value,
        propertySubKey,
        nestedPropertySubKey,
        nestedRowIndex,
        selectedTabSectionKey,
      },
    });
  };

  const handleSelect = (option: ISelectableOption) => {
    if (option.label && !isOptionSelected(option)) {
      setSelectedOptions((prev) => [...prev, option]);
      dispatchSelectedOption(option);
    } else {
      const indexToBeRemoved = selectedOptions
        .map((selectedOption) => selectedOption.value)
        .indexOf(option.value);
      if (indexToBeRemoved < 0) {
        const labelAppendedIndexToBeRemoved = selectedOptions
          .map((selectedOption) => selectedOption.label)
          .indexOf(option.label);
        removeValue(labelAppendedIndexToBeRemoved);
        return;
      }
      removeValue(indexToBeRemoved);
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== KEY_VALUES.ENTER) {
      return;
    }

    handleEnterSelection({
      results,
      selectedOptions,
      defaultOptions,
      setSelectedOptions,
      setResults,
      setInputValue,
      dispatchSelectedOption,
      handleMenu,
    });
  };

  const setOptions = (options: ISelectableOption[]) => {
    setDefaultOptions(options);
    setResults(options);
  };

  const getExistingGroupOption = (): ISelectableOption | null => {
    if (
      !existingGroupIdKey ||
      (existingGroupIdValue !== "" && existingGroupIdValue !== null)
    ) {
      return null;
    }

    const targetKey = existingGroupIdKey.startsWith("$")
      ? existingGroupIdKey.slice(1)
      : existingGroupIdKey;

    const requiredField = getTitleFromTargetPropertyKey({
      targetKey,
      sourceKey: propertyKey,
      rowIndex: nestedRowIndex,
      rowDataReducerState,
    });

    return {
      label: `Select ${requiredField}`,
      value: "",
      disabled: true,
    };
  };

  const getSearchRequest = (searchValue: string) => {
    if (!typeAheadRequest?.body.searchValue) {
      return typeAheadRequest;
    }

    const minSearchCharacters = typeAheadConfig?.minChars ?? 0;

    if (searchValue.length < minSearchCharacters) {
      setResults([
        DROP_DOWN_OPTIONS.MIN_CHARACTERS_FOR_SEARCH(minSearchCharacters),
      ]);

      return null;
    }

    return getSearchValueUpdatedTypeAheadRequest(typeAheadRequest, searchValue);
  };

  const fetchSearchOptions = async (
    searchValue: string = inputValue,
  ): Promise<void> => {
    const existingGroupOption = getExistingGroupOption();

    if (existingGroupOption) {
      setOptions([existingGroupOption]);
      return;
    }

    const emptyRequiredFieldsMessage = getEmptyFieldsMessage(
      dynamicDependentFieldsAndValues,
      propertyKey,
      rowIndex,
      rowDataReducerState,
    );

    if (emptyRequiredFieldsMessage) {
      setOptions([emptyRequiredFieldsMessage]);
      return;
    }

    if (optionsList) {
      setOptions(optionsList);
      return;
    }

    const searchRequest = getSearchRequest(searchValue);

    if (!searchRequest) {
      return;
    }

    setResults([LOADING_DEFAULT_ISelectableOption()]);

    const { options } = await getRecordsBySearchFilter(
      searchRequest,
      evalTypeAheadMethods,
    );

    setOptions(
      options.length > 0 ? options : [NO_RESULTS_DEFAULT_ISelectableOption()],
    );
  };

  const findOptions = (event: ChangeEvent<HTMLInputElement>) => {
    const inputText = event.target.value;

    setInputValue(inputText);
    handleOpen();

    if (typeAheadRequest?.body.searchValue) {
      return;
    }

    const filteredResults = defaultOptions.filter((option) =>
      option.label.toLowerCase().includes(inputText.toLowerCase()),
    );

    setResults(
      filteredResults.length > 0
        ? filteredResults
        : [NO_RESULTS_DEFAULT_ISelectableOption()],
    );

    if (inputText.length === 0) {
      setResults(defaultOptions);
    }
  };

  useEffect(() => {
    if (!optionsList && inputValues.length > 0 && isSelectAllEnabled) {
      void fetchSearchOptions();
    }
  }, [optionsList, isSelectAllEnabled]);

  const handleSelectAll = () => {
    if (isDisabled) return;

    if (isAllSelected) {
      resetSelections();
      return;
    }

    selectableOptions.forEach((option) => {
      if (
        !selectedOptions
          .map((selectedOption) => selectedOption.value)
          .includes(option.value)
      ) {
        dispatchSelectedOption(option);
      }
    });

    setSelectedOptions(selectableOptions);

    setInputValue("");
    setResults(defaultOptions);
  };

  useEffect(() => {
    if (!typeAheadRequest?.body.searchValue) {
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, typeAheadRequest]);

  useEffect(() => {
    if (!isOpen || !typeAheadRequest?.body.searchValue) {
      return;
    }

    void fetchSearchOptions(debouncedInputValue);
  }, [debouncedInputValue, isOpen]);

  const id = propertySubKey ? `-${propertySubKey}` : "";

  const rowId = rowIndex !== undefined ? `-${rowIndex}` : "";

  const formattedId = `${propertyKey}${id}${rowId}-multi-autocomplete-menu`;

  return (
    <>
      <MultiAutocompleteInputWrapper
        data-testid="autocomplete-menu"
        $isDisabled={isDisabled}
      >
        <MultiInputWithTooltip
          dataTestId={formattedId}
          onClear={handleClear}
          id={formattedId}
          label={title}
          labelRef={dimensionRef}
          isOpen={isOpen}
          value={inputValue}
          multiInputValues={
            isAllSelected && isSelectAllEnabled ? ["ALL"] : multiInputValues
          }
          onChange={findOptions}
          onKeyPress={handleKeyPress}
          onClick={handleMenu}
          placeholder={
            isDisabled
              ? title
              : intl.formatMessage(
                  {
                    id: "placeholder.select",
                    defaultMessage: "Select {item}",
                  },
                  {
                    item: title,
                  },
                )
          }
          removeInputValue={isSelectAllEnabled ? resetSelections : removeValue}
          showClearIcon={inputValues.length > 0 && !isDisabled}
          size="small"
          ref={inputRef}
          isRequired={isRequired && !isDisabled}
          isDisabled={isDisabled}
          isInvalid={error || isMaxItemsExceeded}
          errorMessage={isMaxItemsExceeded ? maxItemsErrorMessage : message}
          tooltip={description}
        />
      </MultiAutocompleteInputWrapper>
      <StyledAutocomplete
        dataTestId="autocomplete-menu"
        dimensionRef={dimensionRef}
        labelRef={dimensionRef}
        isAuto={false}
        placement={menuPlacement}
        onClose={handleClose}
        onChange={findOptions}
        onUnselect={onUnselect}
        onOpen={handleOpen}
        isOpen={isOpen}
        style={{
          width: dimensionRef?.current?.offsetWidth,
          maxWidth: dimensionRef?.current?.offsetWidth,
        }}
      >
        {[
          ...(isSelectAllEnabled && hasSelectableOptions
            ? [
                <MenuListItem
                  key="select-all"
                  dataTestId="autocomplete-select-all-menu-list-item"
                  onClick={handleSelectAll}
                  isSelected={isAllSelected}
                  indeterminate={isIndeterminate}
                  showCheckbox
                  showDivider
                >
                  Select All
                </MenuListItem>,
              ]
            : []),

          ...results.map((option, index) => (
            <MenuListItem
              key={uniqueId(JSON.stringify(option.value))}
              title={option.label}
              dataTestId={`autocomplete-menu-list-item-${index}`}
              isSelected={isOptionSelected(option)}
              onClick={() => {
                isSelectingRef.current = true;
                handleSelect(option);
              }}
              isDisabled={option.disabled}
              showCheckbox={!option.disabled}
            >
              {option.label}
            </MenuListItem>
          )),
        ]}
      </StyledAutocomplete>
    </>
  );
}
